import { useRef, useState } from 'react';
import { ActionIcon, Group, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { IconCheck, IconThumbDown, IconThumbUp, IconX } from '@tabler/icons-react';
import { ResponseFeedback } from '~api';
import { api } from '~portal/api';
import { Cmp, logger, trackAction } from '~portal/lib';
import { ResponseActions, ResponseActionsProps } from './MessageActions';

export type ResponseFeedbackTarget = {
  conversationId: string;
  messageId: string;
};

/**
 * @desc Thumbs up/down on one agent response (H2-205).
 *
 * The verdict is persisted the moment a thumb is pressed. Thumbs-down also opens a comment box
 * beneath the response; confirming attaches the comment to the record already written, skipping
 * closes the box and leaves the bare verdict. The thumbs disappear as soon as a verdict exists —
 * optimistically on press, and on load for responses rated in an earlier session.
 */
export function useResponseFeedback({ conversationId, messageId }: ResponseFeedbackTarget) {
  const utils = api.useUtils();
  const givenQuery = api.responseFeedback.forConversation.useQuery(
    { conversationId },
    { staleTime: 5 * 60 * 1_000 },
  );
  const given = givenQuery.data?.find((g) => g.messageId === messageId) ?? null;

  const [commentOpen, setCommentOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The id of the record this component itself wrote — known once the verdict round-trip returns,
  // independently of the query cache catching up.
  const [recordId, setRecordId] = useState<string | null>(null);
  // A comment confirmed before the verdict round-trip has returned is held here and sent as
  // soon as the record id is known.
  const queuedComment = useRef<string | null>(null);
  // Mirrors whether `queuedComment` holds a comment, for render: a ref read during render is
  // invisible to the React Compiler (and does not re-render when it changes).
  const [hasQueuedComment, setHasQueuedComment] = useState(false);

  const commentMutation = api.responseFeedback.comment.useMutation({
    onSuccess: (record) => {
      setCommentOpen(false);
      utils.responseFeedback.forConversation.setData({ conversationId }, (old) =>
        old?.map((g) => (g._id === record._id ? { ...g, comment: record.comment } : g)),
      );
    },
    onError: (err) => {
      setError('Your comment could not be saved. Please try again.');
      logger.error('[ResponseFeedback] comment failed', {
        conversationId,
        messageId,
        error: err.message,
      });
    },
  });

  const recordMutation = api.responseFeedback.record.useMutation({
    onMutate: async ({ verdict }) => {
      await utils.responseFeedback.forConversation.cancel({ conversationId });
      const previous = utils.responseFeedback.forConversation.getData({ conversationId });
      const optimistic: ResponseFeedback.Given = {
        _id: `pending:${messageId}`,
        messageId,
        verdict,
        comment: null,
      };
      utils.responseFeedback.forConversation.setData({ conversationId }, (old) => [
        ...(old ?? []).filter((g) => g.messageId !== messageId),
        optimistic,
      ]);
      return { previous };
    },
    onSuccess: (record) => {
      setRecordId(record._id);
      utils.responseFeedback.forConversation.setData({ conversationId }, (old) =>
        (old ?? []).map((g) =>
          g.messageId === messageId
            ? { _id: record._id, messageId, verdict: record.verdict, comment: record.comment }
            : g,
        ),
      );
      const queued = queuedComment.current;
      if (queued) {
        queuedComment.current = null;
        setHasQueuedComment(false);
        commentMutation.mutate({ feedbackId: record._id, comment: queued });
      }
    },
    onError: (err, _variables, context) => {
      utils.responseFeedback.forConversation.setData({ conversationId }, context?.previous);
      queuedComment.current = null;
      setHasQueuedComment(false);
      setCommentOpen(false);
      setError('Your feedback could not be saved. Please try again.');
      logger.error('[ResponseFeedback] record failed', {
        conversationId,
        messageId,
        error: err.message,
      });
    },
  });

  const give = (verdict: ResponseFeedback.Verdict) => {
    setError(null);
    trackAction('response_feedback_given', {
      rating: verdict === ResponseFeedback.verdicts.Up ? 'up' : 'down',
    });
    if (verdict === ResponseFeedback.verdicts.Down) setCommentOpen(true);
    recordMutation.mutate({ conversationId, messageId, verdict });
  };

  const submitComment = (text: string) => {
    const comment = text.trim();
    if (!comment) return;
    setError(null);
    // Until the record lands `given` is only the optimistic placeholder; the real id arrives via
    // `onSuccess`, which drains the queue.
    const knownId = recordId ?? (given && !given._id.startsWith('pending:') ? given._id : null);
    if (knownId) {
      commentMutation.mutate({ feedbackId: knownId, comment });
    } else {
      queuedComment.current = comment;
      setHasQueuedComment(true);
    }
  };

  const skipComment = () => {
    queuedComment.current = null;
    setHasQueuedComment(false);
    setCommentOpen(false);
  };

  return {
    /** @desc True once any verdict exists for this response — the thumbs are hidden. */
    rated: given !== null,
    commentOpen,
    isSavingComment: commentMutation.isPending || hasQueuedComment,
    error,
    give,
    submitComment,
    skipComment,
  };
}

type FeedbackThumbsProps = {
  onGive: (verdict: ResponseFeedback.Verdict) => void;
};

/** @desc The two verdict buttons, styled to sit beside the copy action. */
export const FeedbackThumbs: Cmp<FeedbackThumbsProps> = function ({ onGive }) {
  return (
    <>
      <Tooltip label="Good response" withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="md"
          aria-label="Good response"
          data-dd-action-name="Rate response up"
          onClick={() => onGive(ResponseFeedback.verdicts.Up)}
        >
          <IconThumbUp size={18} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Poor response" withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="md"
          aria-label="Poor response"
          data-dd-action-name="Rate response down"
          onClick={() => onGive(ResponseFeedback.verdicts.Down)}
        >
          <IconThumbDown size={18} />
        </ActionIcon>
      </Tooltip>
    </>
  );
};

type FeedbackCommentBoxProps = {
  onSubmit: (text: string) => void;
  onSkip: () => void;
  isSaving: boolean;
};

/** @desc Optional comment after a thumbs-down: confirm (check) sends it, skip (cross) closes. */
export const FeedbackCommentBox: Cmp<FeedbackCommentBoxProps> = function ({
  onSubmit,
  onSkip,
  isSaving,
}) {
  const [text, setText] = useState('');
  const canSubmit = text.trim().length > 0 && !isSaving;

  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Textarea
        flex={1}
        size="sm"
        autosize
        minRows={2}
        maxRows={6}
        maxLength={ResponseFeedback.MAX_COMMENT_LENGTH}
        placeholder="What was wrong with this response? (optional)"
        aria-label="Feedback comment"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
            e.preventDefault();
            onSubmit(text);
          }
        }}
        data-autofocus
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the box opens on the user's own thumbs-down press, so focus follows their action rather than landing unannounced
        autoFocus
      />
      <Stack gap={4}>
        <Tooltip label="Send comment" withArrow>
          <ActionIcon
            variant="filled"
            color="blue"
            size="md"
            aria-label="Send comment"
            disabled={!canSubmit}
            loading={isSaving}
            onClick={() => onSubmit(text)}
          >
            <IconCheck size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Skip" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            aria-label="Skip comment"
            onClick={onSkip}
          >
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
      </Stack>
    </Group>
  );
};

export type ResponseActionsWithFeedbackProps = Omit<ResponseActionsProps, 'leading'> &
  ResponseFeedbackTarget;

/**
 * @desc The response action row with the feedback thumbs ahead of the copy action, and the
 * comment box beneath it while a thumbs-down comment is being composed.
 */
export const ResponseActionsWithFeedback: Cmp<ResponseActionsWithFeedbackProps> = function ({
  text,
  timestamp,
  trailing,
  conversationId,
  messageId,
}) {
  const feedback = useResponseFeedback({ conversationId, messageId });

  return (
    <Stack gap="xs">
      <ResponseActions
        text={text}
        timestamp={timestamp}
        leading={!feedback.rated && <FeedbackThumbs onGive={feedback.give} />}
        trailing={trailing}
      />
      {feedback.commentOpen && (
        <FeedbackCommentBox
          onSubmit={feedback.submitComment}
          onSkip={feedback.skipComment}
          isSaving={feedback.isSavingComment}
        />
      )}
      {feedback.error && (
        <Text size="xs" c="red" ta="right">
          {feedback.error}
        </Text>
      )}
    </Stack>
  );
};
